import { useState } from 'react';
import { useHoursMatrix } from '../../../hooks/pricing/useHoursMatrix.js';
import SuspendModal from '../../../components/modales/SuspendModal.jsx';
import ErrorModal from '../../../components/modales/ErrorModal.jsx';
import SuccessModal from '../../../components/modales/SuccessModal.jsx';

export default function EditHoursMatrix() {
    const {
        data,
        loading,
        updateLocalValue,
        saveChanges,
        hasChanges,
        showAlertModal,
        alertConfig,
        askEnableEdit,
        closeAlertModal,
        showErrorModal,
        errorMessage,
        closeErrorModal,
        showSuccessModal,
        successMessage,
        closeSuccessModal
    } = useHoursMatrix();

    const [editEnabled, setEditEnabled] = useState(false);
    const [resourceFilter, setResourceFilter] = useState('');

    const handleToggleEdit = () => {
        if (!editEnabled) {
            askEnableEdit(() => {
                setEditEnabled(true);
                closeAlertModal();
            });
        } else {
            setEditEnabled(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center mt-6">
                <div className="spinner-grow text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // opciones del filtro
    const resourceTypes = [
        ...new Set(data.map(r => r.resourceType.name))
    ];

    // aplicar filtro
    const filteredData = resourceFilter
        ? data.filter(r => r.resourceType.name === resourceFilter)
        : data;

    /* columnas */
    const minutesColumns = [
        ...new Set(filteredData.map(r => r.minutes))
    ].sort((a, b) => a - b);

    /* agrupación */
    const grouped = {};
    filteredData.forEach(row => {
        const key = [
            row.resourceType.name,
            row.multimediaLevel.name,
            row.instructionalLevel.name
        ].join('|');

        if (!grouped[key]) {
            grouped[key] = {
                resourceType: row.resourceType.name,
                multimediaLevel: row.multimediaLevel.name,
                instructionalLevel: row.instructionalLevel.name,
                values: {}
            };
        }
        grouped[key].values[row.minutes] = row;
    });

    const rows = Object.values(grouped);

    return (
        <div className="container-fluid mt-6">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Matriz de horas</h2>
                <div className="form-check form-switch">
                    <input type="checkbox" className="form-check-input" checked={editEnabled} onChange={handleToggleEdit} />
                    <label className="form-check-label"> Habilitar edición </label>
                </div>            
            </div>
            {/* BARRA STICKY SUPERIOR */}
            <div className="bg-body shadow-sm px-3 py-2 mb-3" style={{ position: 'sticky', top: '70px', zIndex: 1050 }} >
                <div className="d-flex align-items-end gap-3">
                    {/* FILTRO – siempre visible */}
                    <div style={{ minWidth: '260px' }}>
                        <label className="form-label fw-semibold"> Filtrar por tipo de recurso </label>
                        <select className="form-select" value={resourceFilter} onChange={e => setResourceFilter(e.target.value)} >
                            <option value="">Todos</option>
                            {resourceTypes.map(type => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* BOTÓN – solo cuando se edita */}
                    <div className="ms-auto">
                        {editEnabled && hasChanges && (
                            <button className="btn btn-success" onClick={saveChanges} > Guardar cambios </button>
                        )}
                    </div>
                </div>
            </div>
            <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                    <thead>
                        <tr>
                            <th rowSpan={2}>Tipo de recurso</th>
                            <th rowSpan={2}>Nivel Multimedia</th>
                            <th rowSpan={2}>Nivel Instruccional</th>
                            <th colSpan={minutesColumns.length} className="text-center"> Duración en minutos </th>
                        </tr>
                        <tr>
                            {minutesColumns.map(min => (
                                <th key={min} className="text-center">{min}</th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {rows.map((row, idx) => (
                            <tr key={idx}>
                                <td>{row.resourceType}</td>
                                <td>{row.multimediaLevel}</td>
                                <td>{row.instructionalLevel}</td>

                                {minutesColumns.map(min => {
                                    const cell = row.values[min];
                                    return (
                                        <td key={min} className="text-center">
                                            {cell ? (
                                                <input type="number" min={0} value={cell.hours_man} disabled={!editEnabled}
                                                    className={`form-control form-control-sm text-center ${ editEnabled ? 'border-info' : '' }`} style={{ minWidth: '55px' }}
                                                    onChange={e => updateLocalValue( cell.id, e.target.value ) }
                                                />
                                            ) : '—'}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* MODALES */}
            <SuspendModal
                show={showAlertModal}
                title={alertConfig.title}
                message={alertConfig.message}
                icon={alertConfig.icon}
                funsuspend={alertConfig.onConfirm}
                onClose={closeAlertModal}
            />
            <ErrorModal
                show={showErrorModal}
                message={errorMessage}
                onClose={closeErrorModal}
            />
            <SuccessModal
                show={showSuccessModal}
                message={successMessage}
                onClose={closeSuccessModal}
            />
        </div>
    );
}